<template>
    <div class="bg-hd-neutral-100 p-3 rounded-lg flex flex-col gap-2 mb-4">
        <div class="flex gap-2">
            <nuxt-img
                v-if="upload.blob"
                :src="upload.blob"
                class="rounded-lg h-12"
            />
            <div
                v-else
                class="h-12 w-12 rounded-lg bg-hd-neutral-200 flex items-center justify-center"
            >
                <FontAwesomeIcon :icon="faFile" />
            </div>
            <div class="flex flex-col flex-1 gap-1">
                <span class="text-sm font-medium">{{ upload.name }}</span>
                <span v-if="status" :class="status.class" class="text-sm">
                    <FontAwesomeIcon :icon="status.icon" :spin="status.spin" />
                    {{
                        upload.status === "error" && upload.error_message
                            ? upload.error_message
                            : status.label
                    }}
                </span>
            </div>
            <div v-if="upload.status === 'error'">
                <UButton color="white" @click="upload.attempt()">Retry</UButton>
            </div>
            <div v-if="['error', 'finished'].includes(upload.status)">
                <UButton color="white" variant="soft" @click="upload.remove()">
                    <FontAwesomeIcon :icon="faTimes" class="text-lg my-[1px]" />
                </UButton>
            </div>
        </div>
        <UProgress
            :animation="upload.status === 'uploading' ? 'carousel' : undefined"
            :color="
                upload.status === 'error'
                    ? 'red'
                    : upload.status === 'pending'
                      ? 'hd-neutral'
                      : 'primary'
            "
            :max="upload.status === 'uploading' ? undefined : 100"
            :value="upload.status === 'uploading' ? undefined : upload.progress"
            size="sm"
        />
    </div>
</template>
<script lang="ts" setup>
import type { MediaLibraryFileUpload } from "~/stores/media-library";
import {
    faCheckCircle,
    faExclamationCircle,
    faFile,
    faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { faSpinnerThird, faTimes } from "@fortawesome/pro-regular-svg-icons";

const props = defineProps<{
    upload: MediaLibraryFileUpload;
}>();

const status = computed(() => {
    if (props.upload.status === "pending") {
        return {
            class: "text-hd-neutral-500",
            icon: faInfoCircle,
            label: "Pending",
        };
    }

    if (props.upload.status === "uploading") {
        return {
            class: "text-hd-neutral-500",
            icon: faSpinnerThird,
            spin: true,
            label: "Uploading",
        };
    }

    if (props.upload.status === "error") {
        return {
            class: "text-red-500",
            icon: faExclamationCircle,
            label: "Upload Failed",
        };
    }

    if (props.upload.status === "finished") {
        return {
            class: "text-primary-500",
            icon: faCheckCircle,
            label: "Upload Complete",
        };
    }

    return undefined;
});
</script>
